import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { influencerStats } from "@/mocks/dropship";
import StarAuthModal from "@/pages/social-stars/components/StarAuthModal";
import { StarUser } from "@/pages/social-stars/components/StarAuthModal";

const platformColors: Record<string, string> = {
  YouTube: "bg-red-100 text-red-600",
  TikTok: "bg-stone-800 text-white",
  Facebook: "bg-sky-100 text-sky-600",
  "Twitter/X": "bg-gray-100 text-gray-700",
};

const platformIcons: Record<string, string> = {
  YouTube: "ri-youtube-line",
  TikTok: "ri-tiktok-line",
  Facebook: "ri-facebook-circle-line",
  "Twitter/X": "ri-twitter-x-line",
};

const steps = [
  { icon: "ri-user-add-line", title: "Sign Up Free", desc: "Register with email or phone number. Upload your profile photo and connect your social channel." },
  { icon: "ri-store-2-line", title: "Pick Products", desc: "Browse our dropship catalog and choose products that match your audience's interests." },
  { icon: "ri-video-line", title: "Share Your Video", desc: "Post videos on Facebook, TikTok, Twitter or YouTube with your unique referral link." },
  { icon: "ri-money-dollar-circle-line", title: "Earn Commission", desc: "Earn up to 22% commission on every sale — tracked in real-time, paid weekly." },
];

export default function SocialStarsSection() {
  const navigate = useNavigate();
  const [authModal, setAuthModal] = useState<"login" | "signup" | null>(null);
  const [loggedUser, setLoggedUser] = useState<StarUser | null>(null);
  const [selectedStar, setSelectedStar] = useState<typeof influencerStats[0] | null>(null);

  const totalSold = influencerStats.reduce((a, s) => a + s.productsSold, 0);
  const totalCommission = influencerStats.reduce((a, s) => a + s.commissionEarned, 0);

  const handleAuthSuccess = (user: StarUser) => {
    setLoggedUser(user);
    setAuthModal(null);
    navigate("/social-stars");
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-screen-2xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
          <div>
            <p className="text-amber-500 text-sm font-semibold uppercase tracking-widest mb-1">Social Stars Dropshipping</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight">
              Facebook · TikTok · Twitter<br className="hidden md:block" /> · YouTube Stars
            </h2>
            <p className="text-gray-500 text-sm mt-2 max-w-xl">
              Social media stars help warehouse suppliers sell dropshipping products by sharing videos — and earn real commission on every sale they drive.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <button
              onClick={() => setAuthModal("signup")}
              className="flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-user-add-line"></i> Join as a Star
            </button>
            <button
              onClick={() => setAuthModal("login")}
              className="flex items-center justify-center gap-2 border-2 border-amber-400 text-amber-500 hover:bg-amber-50 font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-login-box-line"></i> Star Login
            </button>
          </div>
        </div>

        {/* Global stats bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { label: "Total Products Sold", value: totalSold.toLocaleString(), icon: "ri-shopping-bag-3-line", color: "bg-amber-50 text-amber-500" },
            { label: "Total Commission Paid", value: "$" + (totalCommission / 1000).toFixed(0) + "K+", icon: "ri-gift-line", color: "bg-green-50 text-green-500" },
            { label: "Active Social Stars", value: influencerStats.length.toString(), icon: "ri-user-star-line", color: "bg-rose-50 text-rose-500" },
            { label: "Max Commission Rate", value: "22%", icon: "ri-percent-line", color: "bg-orange-50 text-orange-500" },
          ].map((s) => (
            <div key={s.label} className="bg-gray-50 rounded-xl p-4 flex items-center gap-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${s.color}`}>
                <i className={`${s.icon} text-lg`}></i>
              </div>
              <div>
                <div className="text-lg font-extrabold text-gray-900">{s.value}</div>
                <div className="text-xs text-gray-400">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Stars grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {influencerStats.map((star, idx) => (
            <div
              key={star.id}
              onClick={() => setSelectedStar(star)}
              className="bg-gray-50 rounded-2xl overflow-hidden cursor-pointer group hover:-translate-y-1 transition-transform duration-300"
            >
              {/* Top: photo + rank */}
              <div className="relative w-full h-52 overflow-hidden">
                <img
                  src={star.image}
                  alt={star.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>

                {/* Rank badge */}
                <div className={`absolute top-3 left-3 w-8 h-8 flex items-center justify-center rounded-full font-extrabold text-sm ${
                  idx === 0 ? "bg-amber-400 text-white" : idx === 1 ? "bg-gray-300 text-gray-700" : idx === 2 ? "bg-orange-300 text-white" : "bg-white/80 text-gray-600"
                }`}>
                  {idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : `#${idx + 1}`}
                </div>

                {/* Platform badge */}
                <div className={`absolute top-3 right-3 flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${platformColors[star.platform] || "bg-gray-100 text-gray-600"}`}>
                  <i className={platformIcons[star.platform] || "ri-global-line"}></i>
                  {star.platform}
                </div>

                {/* Bottom overlay stats */}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="flex items-end justify-between text-white">
                    <div>
                      <p className="font-extrabold text-base leading-tight">{star.name}</p>
                      <p className="text-white/70 text-xs">{star.flag} {star.country} · {star.followers} followers</p>
                    </div>
                    {star.verified && (
                      <div className="w-6 h-6 flex items-center justify-center bg-green-400 rounded-full shrink-0">
                        <i className="ri-shield-check-fill text-white text-xs"></i>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="p-4">
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="bg-white rounded-xl p-2.5 text-center">
                    <div className="text-sm font-extrabold text-gray-900">{star.productsSold.toLocaleString()}</div>
                    <div className="text-xs text-gray-400">Sold</div>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-2.5 text-center">
                    <div className="text-sm font-extrabold text-amber-500">${(star.totalRevenue / 1000).toFixed(1)}K</div>
                    <div className="text-xs text-gray-400">Revenue</div>
                  </div>
                  <div className="bg-green-50 rounded-xl p-2.5 text-center">
                    <div className="text-sm font-extrabold text-green-600">${star.commissionEarned.toLocaleString()}</div>
                    <div className="text-xs text-gray-400">Earned</div>
                  </div>
                </div>

                {/* Recent sales preview */}
                <div className="flex flex-col gap-1.5 mb-4">
                  {star.recentSales.slice(0, 2).map((sale, si) => (
                    <div key={si} className="flex items-center justify-between text-xs bg-white rounded-lg px-3 py-2">
                      <span className="text-gray-600 truncate flex-1">{sale.product}</span>
                      <span className="text-green-600 font-bold ml-2 shrink-0">+${sale.commission.toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={(e) => { e.stopPropagation(); setSelectedStar(star); }}
                  className="w-full bg-amber-400 hover:bg-amber-500 text-white font-semibold py-2.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  View Full Commission Details
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* How it works */}
        <div className="bg-gray-50 rounded-2xl p-8 mb-10">
          <div className="text-center mb-8">
            <p className="text-amber-500 text-sm font-semibold uppercase tracking-widest mb-1">Earn While You Create</p>
            <h3 className="text-2xl font-extrabold text-gray-900">How to Join & Earn Commission</h3>
            <p className="text-gray-500 text-sm mt-2">Sign up with email or phone number — it takes less than 2 minutes</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {steps.map((s, i) => (
              <div key={s.title} className="flex flex-col items-center text-center">
                <div className="relative mb-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-white rounded-2xl">
                    <i className={`${s.icon} text-amber-500 text-2xl`}></i>
                  </div>
                  <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-amber-400 text-white text-xs font-bold rounded-full">{i + 1}</span>
                </div>
                <h4 className="font-bold text-gray-800 text-sm mb-1">{s.title}</h4>
                <p className="text-gray-500 text-xs leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setAuthModal("signup")}
              className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold px-8 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-user-add-line"></i> Sign Up with Email or Phone
            </button>
            <button
              onClick={() => navigate("/social-stars")}
              className="flex items-center gap-2 border-2 border-gray-200 text-gray-700 hover:border-amber-400 hover:text-amber-500 font-bold px-8 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-right-line"></i> View All Stars
            </button>
          </div>
        </div>

        {/* Platform icons row */}
        <div className="flex flex-wrap justify-center gap-4">
          {[
            { name: "Facebook", icon: "ri-facebook-circle-fill", color: "text-sky-500", bg: "bg-sky-50" },
            { name: "TikTok", icon: "ri-tiktok-fill", color: "text-gray-900", bg: "bg-gray-100" },
            { name: "Twitter / X", icon: "ri-twitter-x-fill", color: "text-gray-800", bg: "bg-gray-100" },
            { name: "YouTube", icon: "ri-youtube-fill", color: "text-red-500", bg: "bg-red-50" },
          ].map((p) => (
            <div key={p.name} className={`flex items-center gap-2.5 ${p.bg} px-5 py-3 rounded-full`}>
              <i className={`${p.icon} text-xl ${p.color}`}></i>
              <span className="text-sm font-bold text-gray-700">{p.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Star detail modal */}
      {selectedStar && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSelectedStar(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <div className="w-full h-48 overflow-hidden rounded-t-2xl">
                <img src={selectedStar.image} alt={selectedStar.name} className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent rounded-t-2xl"></div>
              </div>
              <button
                onClick={() => setSelectedStar(null)}
                className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-black/40 hover:bg-black/60 text-white rounded-full cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
              <div className="absolute bottom-4 left-5 text-white">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${platformColors[selectedStar.platform] || "bg-gray-100 text-gray-600"}`}>
                    <i className={`${platformIcons[selectedStar.platform] || "ri-global-line"} mr-1`}></i>{selectedStar.platform}
                  </span>
                  {selectedStar.verified && (
                    <span className="flex items-center gap-1 text-xs text-amber-300 font-semibold">
                      <i className="ri-shield-check-fill"></i> Verified
                    </span>
                  )}
                </div>
                <h2 className="text-2xl font-extrabold">{selectedStar.name}</h2>
                <p className="text-white/70 text-sm">{selectedStar.flag} {selectedStar.country} · {selectedStar.followers} followers</p>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                  { label: "Commission Rate", value: `${selectedStar.commissionRate}%`, color: "bg-amber-50 text-amber-600" },
                  { label: "Total Earned", value: `$${selectedStar.commissionEarned.toLocaleString()}`, color: "bg-green-50 text-green-600" },
                  { label: "Products Sold", value: selectedStar.productsSold.toLocaleString(), color: "bg-sky-50 text-sky-600" },
                  { label: "Revenue Driven", value: `$${(selectedStar.totalRevenue / 1000).toFixed(0)}K`, color: "bg-rose-50 text-rose-600" },
                ].map((s) => (
                  <div key={s.label} className={`rounded-xl p-3 text-center ${s.color}`}>
                    <div className="text-xl font-extrabold">{s.value}</div>
                    <div className="text-xs opacity-70 mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>

              <div className="bg-amber-50 rounded-xl p-4 mb-5 flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-400 rounded-full shrink-0">
                  <i className="ri-trophy-fill text-white text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-amber-600 font-semibold">Top Selling Product</p>
                  <p className="text-sm font-bold text-gray-800">{selectedStar.topProduct}</p>
                </div>
              </div>

              <h3 className="font-bold text-gray-800 mb-3">Recent Sales &amp; Commissions</h3>
              <div className="flex flex-col gap-2 mb-6">
                {selectedStar.recentSales.map((sale, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{sale.product}</p>
                      <p className="text-xs text-gray-400">{sale.qty} units · {sale.date}</p>
                    </div>
                    <div className="text-right ml-3 shrink-0">
                      <div className="text-sm font-bold text-green-600">+${sale.commission.toFixed(2)}</div>
                      <div className="text-xs text-gray-400">commission</div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => { setSelectedStar(null); setAuthModal("signup"); }}
                className="w-full bg-amber-400 hover:bg-amber-500 text-white font-bold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                Join &amp; Start Earning Like {selectedStar.name.split(" ")[0]}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auth modal */}
      {authModal && (
        <StarAuthModal
          mode={authModal}
          onClose={() => setAuthModal(null)}
          onSuccess={handleAuthSuccess}
        />
      )}
    </section>
  );
}
